import {
    DicomStructureSet,
    RoiInterpretedType,
    ROI_POINT_TYPES,
    DicomContour,
    ContourGeometricType,
} from '../../dicom/dicomstructureset';
import { DicomRoi } from '../../dicom/dicomRoi';
import { DcmJsDatasetWrapper } from '../../dicom/dcmJsWrapper';
import {
    DicomProcessor,
    generateUniqueStructureSetLabel,
} from '../dicomProcessor';
import { Image3D } from '../../patient/image3d';
import { StructureSet } from '../../patient/structureset';
import { DICOMApprovalStatus } from '../../dicom/dicomConstants';
import { RigidRegistration } from '../../patient/rigidRegistration';
import { ReadonlyMat4, mat4, vec3 } from 'gl-matrix';
import { SdfResamplingService } from '~/engine/util/algorithms/sdf/SdfResamplingService';
import { Environment } from '~/engine/data/Environment';

export interface CopyStructureSetParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetImage: Image3D;
    readonly existingStructureSets: readonly StructureSet[];
    readonly selectedRoiNumbers?: Set<number>;
    readonly label?: string;
}

export interface CopyWithRegistrationParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetImage: Image3D;
    readonly registration: RigidRegistration;
    readonly existingStructureSets: readonly StructureSet[];
    readonly selectedRoiNumbers?: Set<number>;
    readonly label?: string;
}

export interface CopyIntoExistingParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetStructureSet: StructureSet;
    readonly selectedRoiNumbers: Set<number>;
    readonly overwriteExisting?: boolean;
}

export interface CopyWithRegistrationIntoExistingParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetStructureSet: StructureSet;
    readonly targetImage: Image3D;
    readonly registration: RigidRegistration;
    readonly selectedRoiNumbers: Set<number>;
    readonly overwriteExisting?: boolean;
    readonly label?: string;
}

export interface CopySameFrameOfReferenceParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetImage: Image3D;
    readonly existingStructureSets: readonly StructureSet[];
    readonly selectedRoiNumbers?: Set<number>;
    readonly label?: string;
}

export interface CopySameFrameOfReferenceIntoExistingParams {
    readonly sourceStructureSet: StructureSet;
    readonly targetStructureSet: StructureSet;
    readonly targetImage: Image3D;
    readonly selectedRoiNumbers: Set<number>;
    readonly overwriteExisting?: boolean;
}

export interface CopyStructureSetResult {
    structureSet: StructureSet;
    copiedRoiNumbers: number[];
    skippedRois: SkippedRoi[];
}

export interface SkippedRoi {
    readonly roiNumber: number;
    readonly roiName: string;
    readonly reason: string;
}

export interface StructureReplacementResult {
    readonly success: boolean;
    readonly newRoiNumber?: number;
    readonly error?: {
        readonly phase: 'validation' | 'creation' | 'deletion' | 'finalization';
        readonly message: string;
        readonly originalError: string;
    };
}

/**
 * Result returned by a {@link ContourResolutionStrategy}.
 *
 * - `contours`  – the resolved contours (already in the target FOR).
 * - `'skip'`    – the ROI should be skipped (no data available).
 */
export type ContourResolutionResult = { contours: DicomContour[] } | 'skip';

/**
 * Strategy that resolves contours for a single ROI during a
 * registration-based copy.
 *
 * Implementations decide *how* to produce target-space contours — e.g. by
 * resampling an SDF texture, transforming point coordinates, or falling back
 * to raw contour vertex transformation.
 *
 * The service calls this once per ROI that needs contour data.
 */
export interface ContourResolutionStrategy {
    resolveContours(
        roiNumber: number,
        sourceRoi: DicomRoi,
        targetImage: Image3D,
        transformMatrix: ReadonlyMat4
    ): Promise<ContourResolutionResult>;
}

/**
 * Resolves contours by resampling the engine's SDF texture for each ROI.
 * Falls back to point transformation for point ROIs, and reports 'skip'
 * when no SDF data is available.
 */
export class SdfContourResolutionStrategy implements ContourResolutionStrategy {
    constructor(private readonly sourceEnvironment: Environment) {}

    async resolveContours(
        roiNumber: number,
        _sourceRoi: DicomRoi,
        targetImage: Image3D,
        transformMatrix: ReadonlyMat4
    ): Promise<ContourResolutionResult> {
        const engineRoi =
            this.sourceEnvironment?.structureSet?.getRoi(roiNumber);
        const sourceTexture = engineRoi?.texture;

        if (!sourceTexture || sourceTexture.isEmpty) {
            if (engineRoi?.isPointRoi && engineRoi.pointCoordinatesMm) {
                const transformed = vec3.transformMat4(
                    vec3.create(),
                    engineRoi.pointCoordinatesMm as vec3,
                    transformMatrix
                );
                return {
                    contours: [
                        new DicomContour(
                            ContourGeometricType.POINT,
                            [transformed],
                            targetImage.sopClassUid,
                            targetImage.imageSlices[0]?.sopInstanceUid ?? ''
                        ),
                    ],
                };
            }
            return 'skip';
        }

        const newContours =
            await SdfResamplingService.resampleAndExtractContours(
                sourceTexture,
                targetImage,
                transformMatrix
            );

        return { contours: newContours };
    }
}

/**
 * Domain service for copying structure sets.
 *
 * Pure domain logic — no UI, persistence, rendering, or framework dependencies.
 */
export class StructureSetCopyService {
    public copySameGeometry(
        params: CopyStructureSetParams
    ): CopyStructureSetResult {
        const {
            sourceStructureSet,
            targetImage,
            existingStructureSets,
            selectedRoiNumbers,
            label,
        } = params;

        const sourceImage = sourceStructureSet.image;
        if (!sourceImage) {
            throw new Error(
                'Cannot copy: source structure set has no associated image.'
            );
        }
        if (!targetImage.frameOfReferenceUid) {
            throw new Error(
                'Cannot copy: target image has no Frame of Reference UID.'
            );
        }

        const sourceAndTargetHaveEqualGeometry =
            sourceImage.grid.hasEqualGeometryTo(targetImage.grid);

        if (!sourceAndTargetHaveEqualGeometry) {
            throw new Error(
                'Cannot copy: source and target images have different geometry.'
            );
        }

        const clonedDicom = this.cloneAndReidentify(
            sourceStructureSet.dicomStructureSet,
            targetImage,
            existingStructureSets,
            label
        );

        const copied = new StructureSet(
            clonedDicom,
            targetImage.frameOfReferenceUid,
            targetImage
        );

        copied.setApprovalStatus(DICOMApprovalStatus.UNAPPROVED, '', true);

        if (selectedRoiNumbers) {
            const toDelete: number[] = [];
            for (const roi of copied) {
                if (!selectedRoiNumbers.has(roi.roiNumber)) {
                    toDelete.push(roi.roiNumber);
                }
            }
            for (const roiNumber of toDelete) {
                copied.dicomStructureSet.deleteRoi(roiNumber);
            }
        }

        const copiedRoiNumbers: number[] = [];
        const skippedRois: SkippedRoi[] = [];

        this.remapAllContourReferences(
            copied,
            sourceImage,
            targetImage,
            copiedRoiNumbers,
            skippedRois
        );

        return { structureSet: copied, copiedRoiNumbers, skippedRois };
    }

    public copyIntoExistingStructureSet(params: CopyIntoExistingParams): {
        copiedRoiNumbers: number[];
        skippedRois: SkippedRoi[];
    } {
        const {
            sourceStructureSet,
            targetStructureSet,
            selectedRoiNumbers,
            overwriteExisting = false,
        } = params;

        if (targetStructureSet.dicomStructureSet.isApproved) {
            throw new Error('Cannot copy into an approved structure set.');
        }

        const copiedRoiNumbers: number[] = [];
        const skippedRois: SkippedRoi[] = [];

        for (const roiNumber of selectedRoiNumbers) {
            const sourceRoi =
                sourceStructureSet.dicomStructureSet.getRoi(roiNumber);
            if (!sourceRoi) {
                skippedRois.push({
                    roiNumber,
                    roiName: `ROI #${roiNumber}`,
                    reason: 'ROI not found in source structure set',
                });
                continue;
            }

            const conflictRoi =
                targetStructureSet.dicomStructureSet.findRoiByTrimmedName(
                    sourceRoi.name
                );

            if (conflictRoi !== undefined) {
                if (!overwriteExisting) {
                    skippedRois.push({
                        roiNumber,
                        roiName: sourceRoi.name,
                        reason: `A structure named "${sourceRoi.name}" already exists in the target`,
                    });
                    continue;
                }

                const replacementResult = this.replaceStructureTransactionally(
                    targetStructureSet,
                    conflictRoi.roiNumber,
                    sourceRoi,
                    [...sourceRoi.contours]
                );

                if (!replacementResult.success) {
                    skippedRois.push({
                        roiNumber,
                        roiName: sourceRoi.name,
                        reason: `Failed to replace existing "${sourceRoi.name}": ${
                            replacementResult.error?.message ?? 'unknown error'
                        }`,
                    });
                    continue;
                }

                if (replacementResult.newRoiNumber === undefined) {
                    skippedRois.push({
                        roiNumber,
                        roiName: sourceRoi.name,
                        reason: `Replacement succeeded but no ROI number returned for "${sourceRoi.name}"`,
                    });
                    continue;
                }

                copiedRoiNumbers.push(replacementResult.newRoiNumber);
                continue;
            }

            try {
                const newRoi = targetStructureSet.dicomStructureSet.createRoi(
                    sourceRoi.interpretedType as RoiInterpretedType,
                    sourceRoi.name
                );

                newRoi.color = {
                    R: sourceRoi.color.R,
                    G: sourceRoi.color.G,
                    B: sourceRoi.color.B,
                };

                newRoi.contours = [...sourceRoi.contours];

                targetStructureSet.dicomStructureSet.updateContourSequence(
                    newRoi.roiNumber
                );

                copiedRoiNumbers.push(newRoi.roiNumber);
            } catch (err) {
                skippedRois.push({
                    roiNumber,
                    roiName: sourceRoi.name,
                    reason:
                        err instanceof Error
                            ? err.message
                            : 'Unknown error while copying ROI',
                });
            }
        }

        return { copiedRoiNumbers, skippedRois };
    }

    /**
     * Copies a structure set onto a target image linked via a rigid
     * registration. A {@link ContourResolutionStrategy} controls
     * how contour data is produced for each ROI (e.g. SDF resampling).
     */
    public async copyWithRegistration(
        params: CopyWithRegistrationParams,
        contourStrategy: ContourResolutionStrategy
    ): Promise<CopyStructureSetResult> {
        const { sourceStructureSet, targetImage, registration } = params;

        const sourceImage = sourceStructureSet.image;
        if (!sourceImage) {
            throw new Error(
                'Cannot copy: source structure set has no associated image.'
            );
        }
        if (!targetImage.frameOfReferenceUid) {
            throw new Error(
                'Cannot copy: target image has no Frame of Reference UID.'
            );
        }

        const transformMatrix = registration.getTransformFromTo(
            sourceImage.frameOfReferenceUid,
            targetImage.frameOfReferenceUid
        );
        if (!transformMatrix) {
            throw new Error(
                'Registration does not provide a transformation between the source and target images.'
            );
        }

        return this.copyWithTransform(
            sourceStructureSet,
            targetImage,
            transformMatrix,
            contourStrategy,
            params.existingStructureSets,
            params.selectedRoiNumbers,
            params.label
        );
    }

    /**
     * Copies a structure set to a target image that shares the same Frame
     * of Reference but has different geometry. Uses an identity transform
     * since same-FOR images are already in the same patient coordinate space.
     */
    public async copySameFrameOfReference(
        params: CopySameFrameOfReferenceParams,
        contourStrategy: ContourResolutionStrategy
    ): Promise<CopyStructureSetResult> {
        const { sourceStructureSet, targetImage } = params;

        const sourceImage = sourceStructureSet.image;
        if (!sourceImage) {
            throw new Error(
                'Cannot copy: source structure set has no associated image.'
            );
        }
        if (!targetImage.frameOfReferenceUid) {
            throw new Error(
                'Cannot copy: target image has no Frame of Reference UID.'
            );
        }

        if (
            sourceImage.frameOfReferenceUid !== targetImage.frameOfReferenceUid
        ) {
            throw new Error(
                'Cannot use same-FOR copy: source and target images have different ' +
                    `Frames of Reference (${sourceImage.frameOfReferenceUid} vs ` +
                    `${targetImage.frameOfReferenceUid}). ` +
                    'Use copyWithRegistration instead.'
            );
        }

        return this.copyWithTransform(
            sourceStructureSet,
            targetImage,
            mat4.create(),
            contourStrategy,
            params.existingStructureSets,
            params.selectedRoiNumbers,
            params.label
        );
    }

    /**
     * Copies selected ROIs from a source structure set into an existing
     * target structure set, using a rigid registration to transform contours.
     *
     * The {@link ContourResolutionStrategy} controls how contour data is
     * produced (SDF resampling, etc.).
     */
    public async copyWithRegistrationIntoExisting(
        params: CopyWithRegistrationIntoExistingParams,
        contourStrategy: ContourResolutionStrategy
    ): Promise<CopyStructureSetResult> {
        const {
            sourceStructureSet,
            targetStructureSet,
            targetImage,
            registration,
            selectedRoiNumbers,
            overwriteExisting = false,
        } = params;

        const sourceImage = sourceStructureSet.image;
        if (!sourceImage) {
            throw new Error(
                'Cannot copy: source structure set has no associated image.'
            );
        }

        const transformMatrix = registration.getTransformFromTo(
            sourceImage.frameOfReferenceUid,
            targetImage.frameOfReferenceUid
        );
        if (!transformMatrix) {
            throw new Error(
                'Registration does not provide a transformation between the source and target images.'
            );
        }

        return this.copyWithTransformIntoExisting(
            sourceStructureSet,
            targetStructureSet,
            targetImage,
            transformMatrix,
            contourStrategy,
            selectedRoiNumbers,
            overwriteExisting
        );
    }

    /**
     * Copies selected ROIs into an existing target structure set on an
     * image that shares the same Frame of Reference but has different
     * geometry. Uses an identity transform.
     */
    public async copySameFrameOfReferenceIntoExisting(
        params: CopySameFrameOfReferenceIntoExistingParams,
        contourStrategy: ContourResolutionStrategy
    ): Promise<CopyStructureSetResult> {
        const {
            sourceStructureSet,
            targetStructureSet,
            targetImage,
            selectedRoiNumbers,
            overwriteExisting = false,
        } = params;

        const sourceImage = sourceStructureSet.image;
        if (!sourceImage) {
            throw new Error(
                'Cannot copy: source structure set has no associated image.'
            );
        }

        if (!targetImage.frameOfReferenceUid) {
            throw new Error(
                'Cannot copy: target image has no Frame of Reference UID.'
            );
        }

        if (
            sourceImage.frameOfReferenceUid !== targetImage.frameOfReferenceUid
        ) {
            throw new Error(
                'Cannot use same-FOR copy: source and target images have different ' +
                    `Frames of Reference (${sourceImage.frameOfReferenceUid} vs ` +
                    `${targetImage.frameOfReferenceUid}). ` +
                    'Use copyWithRegistrationIntoExisting instead.'
            );
        }

        return this.copyWithTransformIntoExisting(
            sourceStructureSet,
            targetStructureSet,
            targetImage,
            mat4.create(),
            contourStrategy,
            selectedRoiNumbers,
            overwriteExisting
        );
    }

    /**
     * Internal: copies a structure set to a new target image using the
     * given transform matrix and contour resolution strategy.
     */
    private async copyWithTransform(
        sourceStructureSet: StructureSet,
        targetImage: Image3D,
        transformMatrix: ReadonlyMat4,
        contourStrategy: ContourResolutionStrategy,
        existingStructureSets: readonly StructureSet[],
        selectedRoiNumbers?: Set<number>,
        label?: string
    ): Promise<CopyStructureSetResult> {
        const clonedDicom = this.cloneAndReidentify(
            sourceStructureSet.dicomStructureSet,
            targetImage,
            existingStructureSets,
            label
        );

        const copied = new StructureSet(
            clonedDicom,
            targetImage.frameOfReferenceUid,
            targetImage
        );

        copied.setApprovalStatus(DICOMApprovalStatus.UNAPPROVED, '', true);

        if (selectedRoiNumbers) {
            const toDelete: number[] = [];
            for (const roi of copied) {
                if (!selectedRoiNumbers.has(roi.roiNumber)) {
                    toDelete.push(roi.roiNumber);
                }
            }
            for (const roiNumber of toDelete) {
                copied.dicomStructureSet.deleteRoi(roiNumber);
            }
        }

        const copiedRoiNumbers: number[] = [];
        const skippedRois: SkippedRoi[] = [];
        const roiNumbersToDelete: number[] = [];

        for (const roi of copied) {
            const sourceRoi = sourceStructureSet.dicomStructureSet.getRoi(
                roi.roiNumber
            );
            if (!sourceRoi) {
                continue;
            }

            try {
                const result = await contourStrategy.resolveContours(
                    roi.roiNumber,
                    sourceRoi,
                    targetImage,
                    transformMatrix
                );

                if (result === 'skip') {
                    skippedRois.push({
                        roiNumber: roi.roiNumber,
                        roiName: roi.name,
                        reason: 'Contour resolution strategy skipped this ROI',
                    });
                    roiNumbersToDelete.push(roi.roiNumber);
                    continue;
                }

                roi.contours = result.contours;
                copied.dicomStructureSet.updateContourSequence(roi.roiNumber);
                copiedRoiNumbers.push(roi.roiNumber);
            } catch (err) {
                skippedRois.push({
                    roiNumber: roi.roiNumber,
                    roiName: roi.name,
                    reason:
                        err instanceof Error
                            ? err.message
                            : 'Unknown error during contour resolution',
                });
                roiNumbersToDelete.push(roi.roiNumber);
            }
        }

        for (const roiNumber of roiNumbersToDelete) {
            copied.dicomStructureSet.deleteRoi(roiNumber);
        }

        copied.roiArray.forEach((r: DicomRoi) => {
            r.approvalStatus = DICOMApprovalStatus.UNAPPROVED;
        });

        return { structureSet: copied, copiedRoiNumbers, skippedRois };
    }

    /**
     * Internal: copies selected ROIs into an existing target structure set
     * using the given transform matrix and contour resolution strategy.
     */
    private async copyWithTransformIntoExisting(
        sourceStructureSet: StructureSet,
        targetStructureSet: StructureSet,
        targetImage: Image3D,
        transformMatrix: ReadonlyMat4,
        contourStrategy: ContourResolutionStrategy,
        selectedRoiNumbers: Set<number>,
        overwriteExisting: boolean
    ): Promise<CopyStructureSetResult> {
        if (targetStructureSet.dicomStructureSet.isApproved) {
            throw new Error('Cannot copy into an approved structure set.');
        }

        const copiedRoiNumbers: number[] = [];
        const skippedRois: SkippedRoi[] = [];

        for (const roiNumber of selectedRoiNumbers) {
            const sourceRoi =
                sourceStructureSet.dicomStructureSet.getRoi(roiNumber);
            if (!sourceRoi) {
                skippedRois.push({
                    roiNumber,
                    roiName: `ROI #${roiNumber}`,
                    reason: 'ROI not found in source structure set',
                });
                continue;
            }

            const conflictRoi =
                targetStructureSet.dicomStructureSet.findRoiByTrimmedName(
                    sourceRoi.name
                );

            if (conflictRoi !== undefined) {
                if (!overwriteExisting) {
                    skippedRois.push({
                        roiNumber,
                        roiName: sourceRoi.name,
                        reason: `A structure named "${sourceRoi.name}" already exists in the target`,
                    });
                    continue;
                }

                let resolvedContours: DicomContour[];
                try {
                    const result = await contourStrategy.resolveContours(
                        roiNumber,
                        sourceRoi,
                        targetImage,
                        transformMatrix
                    );

                    if (result === 'skip') {
                        skippedRois.push({
                            roiNumber,
                            roiName: sourceRoi.name,
                            reason: 'Contour resolution strategy skipped this ROI',
                        });
                        continue;
                    }

                    resolvedContours = result.contours;
                } catch (err) {
                    skippedRois.push({
                        roiNumber,
                        roiName: sourceRoi.name,
                        reason:
                            err instanceof Error
                                ? err.message
                                : 'Contour resolution failed',
                    });
                    continue;
                }

                const replacementResult = this.replaceStructureTransactionally(
                    targetStructureSet,
                    conflictRoi.roiNumber,
                    sourceRoi,
                    resolvedContours
                );

                if (!replacementResult.success) {
                    skippedRois.push({
                        roiNumber,
                        roiName: sourceRoi.name,
                        reason: `Failed to replace existing "${sourceRoi.name}": ${
                            replacementResult.error?.message ?? 'unknown error'
                        }`,
                    });
                    continue;
                }

                if (replacementResult.newRoiNumber === undefined) {
                    skippedRois.push({
                        roiNumber,
                        roiName: sourceRoi.name,
                        reason: `Replacement succeeded but no ROI number returned for "${sourceRoi.name}"`,
                    });
                    continue;
                }

                copiedRoiNumbers.push(replacementResult.newRoiNumber);
                continue;
            }

            let resolvedContours: DicomContour[];
            try {
                const result = await contourStrategy.resolveContours(
                    roiNumber,
                    sourceRoi,
                    targetImage,
                    transformMatrix
                );

                if (result === 'skip') {
                    skippedRois.push({
                        roiNumber,
                        roiName: sourceRoi.name,
                        reason: 'Contour resolution strategy skipped this ROI',
                    });
                    continue;
                }

                resolvedContours = result.contours;
            } catch (err) {
                skippedRois.push({
                    roiNumber,
                    roiName: sourceRoi.name,
                    reason:
                        err instanceof Error
                            ? err.message
                            : 'Contour resolution failed',
                });
                continue;
            }

            try {
                const newRoi = targetStructureSet.dicomStructureSet.createRoi(
                    sourceRoi.interpretedType as RoiInterpretedType,
                    sourceRoi.name
                );

                newRoi.color = {
                    R: sourceRoi.color.R,
                    G: sourceRoi.color.G,
                    B: sourceRoi.color.B,
                };

                newRoi.contours = resolvedContours;
                targetStructureSet.dicomStructureSet.updateContourSequence(
                    newRoi.roiNumber
                );

                copiedRoiNumbers.push(newRoi.roiNumber);
            } catch (err) {
                skippedRois.push({
                    roiNumber,
                    roiName: sourceRoi.name,
                    reason:
                        err instanceof Error
                            ? err.message
                            : 'Unknown error while copying ROI',
                });
            }
        }

        return {
            structureSet: targetStructureSet,
            copiedRoiNumbers,
            skippedRois,
        };
    }

    public static buildSliceNormalPositionLookup(image: Image3D): {
        normalPosition: number;
        sopInstanceUid: string;
        sopClassUid: string;
    }[] {
        const normalDir = image.grid.getUnitZ();
        return image.imageSlices
            .map((slice) => ({
                normalPosition: vec3.dot(normalDir, slice.imagePosition),
                sopInstanceUid: slice.sopInstanceUid,
                sopClassUid: image.sopClassUid,
            }))
            .sort((a, b) => a.normalPosition - b.normalPosition);
    }

    private cloneAndReidentify(
        sourceDicom: DicomStructureSet,
        targetImage: Image3D,
        existingStructureSets: readonly StructureSet[],
        labelOverride?: string
    ): DicomStructureSet {
        const clonedWrapper = new DcmJsDatasetWrapper(
            DicomProcessor.cloneDicomDataset(sourceDicom)
        );

        const newSopUid = DicomProcessor.generateUid();
        const newSeriesUid = DicomProcessor.generateUid();

        const targetSliceUids = targetImage.imageSlices.map(
            (slice) => slice.sopInstanceUid
        );

        DicomProcessor.setSopInstanceUid(clonedWrapper, newSopUid);
        DicomProcessor.setSeriesInstanceUid(clonedWrapper, newSeriesUid);
        DicomProcessor.setFrameOfReferenceUid(
            clonedWrapper,
            targetImage.frameOfReferenceUid
        );
        DicomProcessor.setReferencedSeriesUid(
            clonedWrapper,
            targetImage.seriesUid
        );
        DicomProcessor.setApprovalStatus(
            clonedWrapper,
            DICOMApprovalStatus.UNAPPROVED
        );

        this.updateReferencedImageSequence(
            clonedWrapper,
            targetImage.seriesUid,
            targetImage.sopClassUid,
            targetSliceUids
        );

        const parsedRois = DicomProcessor.parseRoisFromDataset(clonedWrapper);

        const newLabel =
            labelOverride ??
            generateUniqueStructureSetLabel(
                [...existingStructureSets],
                sourceDicom.structureSetLabel
            );

        const clonedDicom = new DicomStructureSet(
            clonedWrapper,
            newSopUid,
            targetImage.frameOfReferenceUid,
            targetImage.seriesUid,
            parsedRois,
            targetSliceUids,
            targetImage.sopClassUid,
            newSeriesUid
        );

        clonedDicom.structureSetLabel = newLabel;
        return clonedDicom;
    }

    private updateReferencedImageSequence(
        dataset: DcmJsDatasetWrapper,
        seriesUid: string,
        sopClassUid: string,
        sopInstanceUids: string[]
    ): void {
        DicomProcessor.updateReferencedImageUids(
            dataset,
            seriesUid,
            sopClassUid,
            sopInstanceUids
        );
    }

    private remapAllContourReferences(
        copied: StructureSet,
        sourceImage: Image3D,
        targetImage: Image3D,
        outCopied: number[],
        outSkipped: SkippedRoi[]
    ): void {
        for (const roi of copied) {
            const isPoint =
                roi.interpretedType !== undefined &&
                ROI_POINT_TYPES.includes(
                    roi.interpretedType as RoiInterpretedType
                );

            if (isPoint) {
                outCopied.push(roi.roiNumber);
                continue;
            }

            try {
                this.remapRoiContourReferences(
                    roi,
                    sourceImage,
                    targetImage,
                    copied.dicomStructureSet
                );
                outCopied.push(roi.roiNumber);
            } catch (err) {
                outSkipped.push({
                    roiNumber: roi.roiNumber,
                    roiName: roi.name,
                    reason:
                        err instanceof Error
                            ? err.message
                            : 'Unknown error during contour remapping',
                });
            }
        }
    }

    private remapRoiContourReferences(
        roi: DicomRoi,
        sourceImage: Image3D,
        targetImage: Image3D,
        targetDicom: DicomStructureSet
    ): void {
        const remapped = roi.contours.map((contour) => {
            if (!contour.referencedSOPInstanceUID) {
                return contour;
            }

            const sourceSliceZ = this.getSlicePositionForUid(
                sourceImage,
                contour.referencedSOPInstanceUID
            );
            if (sourceSliceZ === undefined) {
                throw new Error(
                    `Source slice not found for SOP UID ${contour.referencedSOPInstanceUID}`
                );
            }

            const targetUid = targetImage.getUidForSlice(sourceSliceZ);
            if (!targetUid) {
                throw new Error(`No target slice at z = ${sourceSliceZ} mm`);
            }

            return contour.withReferencedUids(
                targetDicom.referencedSopClassUid,
                targetUid
            );
        });

        roi.contours = remapped;
        targetDicom.updateContourSequence(roi.roiNumber);
    }

    private getSlicePositionForUid(
        image: Image3D,
        sopInstanceUid: string
    ): number | undefined {
        const slice = image.imageSlices.find(
            (s) => s.sopInstanceUid === sopInstanceUid
        );
        if (!slice) {
            return undefined;
        }
        const normalDir = image.grid.getUnitZ();
        return vec3.dot(normalDir, slice.imagePosition);
    }

    /**
     * Transactionally replaces an existing structure with a new one.
     * Creates the new structure first, then removes the old one only if creation succeeds.
     * This prevents data loss if the replacement process fails.
     *
     * @param targetStructureSet The structure set to modify
     * @param existingRoiNumber ROI number of the structure to replace
     * @param sourceRoi Source ROI to copy properties from
     * @param contours Contours for the new structure
     * @returns Result indicating success/failure and details
     */
    private replaceStructureTransactionally(
        targetStructureSet: StructureSet,
        existingRoiNumber: number,
        sourceRoi: DicomRoi,
        contours: DicomContour[]
    ): StructureReplacementResult {
        try {
            this.validateReplacementInputs(sourceRoi, contours);
        } catch (err) {
            return {
                success: false,
                error: {
                    phase: 'validation',
                    message: 'Pre-validation failed',
                    originalError:
                        err instanceof Error
                            ? err.message
                            : 'Unknown validation error',
                },
            };
        }

        const originalRoi =
            targetStructureSet.dicomStructureSet.getRoi(existingRoiNumber);
        if (!originalRoi) {
            return {
                success: false,
                error: {
                    phase: 'validation',
                    message: 'Original structure not found',
                    originalError: `ROI ${existingRoiNumber} does not exist`,
                },
            };
        }

        const tempName = `${sourceRoi.name}_temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        let tempRoi: DicomRoi;

        try {
            tempRoi = targetStructureSet.dicomStructureSet.createRoi(
                sourceRoi.interpretedType as RoiInterpretedType,
                tempName
            );

            tempRoi.color = {
                R: sourceRoi.color.R,
                G: sourceRoi.color.G,
                B: sourceRoi.color.B,
            };

            tempRoi.contours = contours;
            tempRoi.approvalStatus = DICOMApprovalStatus.UNAPPROVED;

            targetStructureSet.dicomStructureSet.updateContourSequence(
                tempRoi.roiNumber
            );
        } catch (err) {
            return {
                success: false,
                error: {
                    phase: 'creation',
                    message: 'Failed to create replacement structure',
                    originalError:
                        err instanceof Error
                            ? err.message
                            : 'Unknown creation error',
                },
            };
        }

        try {
            targetStructureSet.dicomStructureSet.deleteRoi(existingRoiNumber);
        } catch (err) {
            try {
                targetStructureSet.dicomStructureSet.deleteRoi(
                    tempRoi.roiNumber
                );
            } catch (cleanupErr) {
                console.warn(
                    'Failed to cleanup temporary structure during rollback:',
                    cleanupErr
                );
            }

            return {
                success: false,
                error: {
                    phase: 'deletion',
                    message:
                        'Failed to remove original structure, replacement rolled back',
                    originalError:
                        err instanceof Error
                            ? err.message
                            : 'Unknown deletion error',
                },
            };
        }

        try {
            tempRoi.name = sourceRoi.name;
            targetStructureSet.dicomStructureSet.updateContourSequence(
                tempRoi.roiNumber
            );

            return {
                success: true,
                newRoiNumber: tempRoi.roiNumber,
            };
        } catch (err) {
            console.error(
                'Failed to finalize structure name, structure exists with temporary name:',
                tempName
            );

            return {
                success: true,
                newRoiNumber: tempRoi.roiNumber,
            };
        }
    }

    /**
     * Validates inputs for structure replacement to catch issues early.
     *
     * Note: `interpretedType` is validated with an explicit `undefined`
     * check rather than a falsy check because `RoiInterpretedType.UNKNOWN`
     * is defined as `''` (empty string) — a valid and common state in
     * real DICOM data that should not be rejected.
     */
    private validateReplacementInputs(
        sourceRoi: DicomRoi,
        contours: DicomContour[]
    ): void {
        if (!sourceRoi) {
            throw new Error('Source ROI is required');
        }

        if (!sourceRoi.name || sourceRoi.name.trim().length === 0) {
            throw new Error('Source ROI must have a valid name');
        }

        if (
            sourceRoi.interpretedType === undefined ||
            sourceRoi.interpretedType === null
        ) {
            throw new Error('Source ROI must have an interpreted type');
        }

        if (!Array.isArray(contours)) {
            throw new Error('Contours must be an array');
        }
    }
}
